import { useCallback, useMemo, useState } from 'react';
import { useStore } from '../../contexts/StoreContext';
import { ILookupModel } from 'src/store/_models/lookup';
import { useActiveLookupContext } from '../../contexts/ActiveLookupContext';
import Datadog from '../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import { ToolbarContainerProps, ToolbarProps } from './Toolbar.types';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { LookupCodes } from '../../constants/lookup.constants';
import { useProductLogic } from 'src/hooks/useProduct';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';

const ACTIVE_LOOKUPS = new Set<LookupCodes>([
  LookupCodes.FORMAT,
  LookupCodes.SIDE_TICKNESS,
  LookupCodes.BORDER_TYPE,
  LookupCodes.KIT_TYPE,
  LookupCodes.FRAME_COLOR,
  LookupCodes.LAYOUT,
  LookupCodes.FRONT_SURFACE,
  LookupCodes.PASSEPARTOUT,
  LookupCodes.PASSEPARTOUT_COLOR,
  LookupCodes.PASSEPARTOUT_TYPE,
  LookupCodes.CORRECTION,
  LookupCodes.SIDE_COLOR,
  LookupCodes.PAPER_COLOR,
  LookupCodes.TEXT,
]);

export function useToolbarLogic({ onLayout, onActiveToolChange, product }: ToolbarContainerProps): ToolbarProps {
  const {
    i18n,
    project: {
      activeProduct,
      activeTool,
      setActiveTool: _setActiveTool,
      activeUserImageArea,
      activeProductId,
      nextActiveProductId,
      isPreviewModeActive,
      isEditModeActive,
      isProductEditModeActive,
      isProductSetProject,
    },
    bottomSheet: { openBottomSheet, closeBottomSheet },
    dynamicModal: { openModal, closeModal },
  } = useStore();

  const setActiveTool = (tool: ACTIVE_TOOLS) => {
    _setActiveTool(tool);
    onActiveToolChange && onActiveToolChange(tool);
  };
  const currentProduct = product ?? activeProduct;

  const { getProductLookupTitle } = useProductLogic(currentProduct);

  const { activeLookup, setActiveLookup } = useActiveLookupContext();
  const [isSecondLevelLookupActive, setIsSecondLevelLookupActive] = useState<boolean>(false);

  const { isToolbarLookupsVisible, lookupsFirstLevel, lookupsSecondLevel } = useMemo(() => {
    let lookupsFirstLevel: Array<ILookupModel> = [];
    let lookupsSecondLevel: Array<ILookupModel> = [];
    if (currentProduct) {
      const lookups = currentProduct.lookups.filter((lookup: ILookupModel) =>
        ACTIVE_LOOKUPS.has(lookup.code as LookupCodes),
      );
      lookupsFirstLevel = lookups.slice(0, 4);
      lookupsSecondLevel = lookups.slice(4);
    }

    return {
      isToolbarLookupsVisible: !!currentProduct,
      lookupsFirstLevel,
      lookupsSecondLevel,
    };
  }, [currentProduct]);

  const isActiveLookup = useCallback(
    (lookup_code: string) => {
      return activeLookup === lookup_code;
    },
    [activeLookup],
  );

  const handleClose = useCallback(() => {
    setActiveLookup('');
    setIsSecondLevelLookupActive(false);
  }, []);

  const onPressSecondLevelLookup = useCallback(
    (lookup: ILookupModel) => {
      setActiveLookup(lookup.code);
      setIsSecondLevelLookupActive(true);
    },
    [setActiveLookup, setIsSecondLevelLookupActive],
  );

  const onPressBackSecondLevelLookup = useCallback(() => {
    setActiveLookup('secondLevel');
    setIsSecondLevelLookupActive(false);
  }, [setActiveLookup, setIsSecondLevelLookupActive]);

  const openBottomSheetLookupWithOptions = useCallback(
    (lookup: ILookupModel) => {
      openBottomSheet({
        bottomSheetView: BOTTOM_SHEET_VIEWS.LookupOptions,
        bottomSheetTitle: lookup.code,
        bottomSheetLookup: lookup,
      });
      Datadog.startMeasure(DATADOG_CUSTOM_ACTIONS.configuration_options_loading_time);
    },
    [openBottomSheet],
  );

  return {
    i18n,
    handleClose,
    isActiveLookup,
    lookupsFirstLevel,
    lookupsSecondLevel,
    getActiveLookupTitle: getProductLookupTitle,
    onPressSecondLevelLookup,
    onPressBackSecondLevelLookup,
    isSecondLevelLookupActive,
    setActiveLookup,
    activeLookup,
    setIsSecondLevelLookupActive,
    isToolbarLookupsVisible,
    openBottomSheetLookupWithOptions,
    currentProduct,
    activeTool,
    activeProductId,
    nextActiveProductId,
    setActiveTool,
    activeUserImageArea,
    onLayout,
    openBottomSheet,
    closeBottomSheet,
    isPreviewModeActive,
    isEditModeActive,
    isProductEditModeActive,
    openModal,
    closeModal,
    isProductSetProject,
  };
}
